import { GOOD, PRODUCTS, fmtLitres } from './constants'

// The butterfly-valve glyph from the spec: two opposed triangles.
function ValveIcon() {
  return (
    <svg width="14" height="10" viewBox="0 0 14 10" fill="currentColor" aria-hidden="true">
      <path d="M1 1v8l5-4z" /><path d="M13 1v8l-5-4z" />
    </svg>
  )
}

export function ValvePill({ open, onToggle, busy }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      title={open
        ? 'Valve open — click to close (at least one must stay open)'
        : 'Valve closed — click to open this tank for selling'}
      className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[.05em] transition-colors disabled:opacity-60 ${
        open
          ? 'border-[#0f9d63]/40 bg-[#0f9d63]/10 text-[#0f9d63]'
          : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-600'
      }`}
      style={open ? { color: GOOD } : undefined}
    >
      <ValveIcon />
      {open ? 'Selling' : 'Reserve'}
    </button>
  )
}

/**
 * One storage tank, drawn as a silo filled to its last measured level.
 * `fill` is 0–1, or null when the tank has never been dipped — in which case
 * the silo reads empty with a dash rather than a misleading 0%.
 */
export default function Silo({ tank, product, fill, onOpen, onToggleValve, busy }) {
  const color = PRODUCTS[product]?.color || '#64748b'
  const pct = fill == null ? null : Math.round(fill * 100)
  const valveState = tank.valveOpen ? 'selling' : 'on reserve'

  return (
    <div className="flex w-[98px] flex-shrink-0 flex-col items-center">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Tank ${tank.tankNumber}, ${fmtLitres(tank.capacity)} litres capacity, ${pct == null ? 'no readings yet' : pct + ' percent full'}, ${valveState}. Open tank settings.`}
        className="group flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2932b] focus-visible:ring-offset-2 rounded-lg"
      >
        <div
          className="relative h-[150px] w-[88px] overflow-hidden rounded-t-[30px] rounded-b-[9px] border border-gray-200 bg-white shadow-sm transition group-hover:-translate-y-0.5 group-hover:shadow-md"
          style={{ opacity: tank.valveOpen ? 1 : 0.62 }}
        >
          {/* quarter-level gauge bands */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: 'repeating-linear-gradient(to top, transparent 0 24.9%, #d7e0dc 24.9% 25.4%)' }}
          />
          {fill != null && (
            <div
              className="absolute inset-x-0 bottom-0"
              style={{ height: `${pct}%`, background: `linear-gradient(180deg, ${color}, color-mix(in srgb, ${color} 70%, black))` }}
            />
          )}
          <span
            className="absolute inset-x-0 top-2 text-center font-mono text-[10.5px] font-semibold"
            style={{ color: fill == null ? '#7c928c' : '#fff', textShadow: fill == null ? 'none' : '0 1px 2px rgba(0,0,0,.4)' }}
          >
            {pct == null ? '—' : `${pct}%`}
          </span>
        </div>
        <span className="mt-2.5 text-[12.5px] font-semibold text-gray-800">Tank {tank.tankNumber}</span>
        <span className="mt-px font-mono text-[10.5px] text-gray-400">{fmtLitres(tank.capacity)} L</span>
      </button>

      <ValvePill open={tank.valveOpen} busy={busy} onToggle={onToggleValve} />
      <span className="mt-2 h-3.5 w-px bg-gray-300" />
    </div>
  )
}

/** Dashed silo-shaped button that adds another tank to the line. */
export function AddSilo({ onClick }) {
  return (
    <div className="flex w-[98px] flex-shrink-0 flex-col items-center">
      <button
        type="button"
        onClick={onClick}
        className="flex h-[110px] w-[88px] flex-col items-center justify-center gap-2 rounded-t-[30px] rounded-b-[9px] border-[1.5px] border-dashed border-gray-300 text-gray-400 transition hover:border-[#e2932b] hover:bg-[#e2932b]/10 hover:text-[#e2932b]"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] border-current text-base leading-none">+</span>
        <span className="text-[11px] font-semibold">Add tank</span>
      </button>
    </div>
  )
}
